// ============================================================================
// ecgStudyFilters.ts — отборы, поиск и сортировка очереди исследований ЭКГ
// (чистые функции, без UI). Серверный ListTableData умеет только точное
// равенство, поэтому текстовый поиск/качество/заключение и порядок считаются
// на загруженной странице доступных исследований.
// ============================================================================

import { formatValue } from '@/lib/format';
import type { EcgQualityVerdict, EcgStudyListItem } from '@/lib/ecgStudies';
import { patientDisplayName, patientOfStudy } from '@/lib/ecgPatients';

export type EcgStudySort =
    | 'date_desc'
    | 'date_asc'
    | 'hr_desc'
    | 'hr_asc'
    | 'quality_best'
    | 'quality_worst';

export interface EcgStudyFilterState {
    /** Текстовый поиск: пациент, название исследования, заключение. */
    search: string;
    /** Заключение врача: пусто — любое, yes — есть, no — ожидает. */
    conclusion: '' | 'yes' | 'no';
    dateFrom: string;
    dateTo: string;
    status: string;
    quality: '' | EcgQualityVerdict;
}

export const EMPTY_ECG_STUDY_FILTERS: EcgStudyFilterState = {
    search: '',
    conclusion: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    quality: '',
};

export const ECG_STUDY_SORT_OPTIONS: Array<{ value: EcgStudySort; label: string }> = [
    { value: 'date_desc', label: 'Сначала новые' },
    { value: 'date_asc', label: 'Сначала старые' },
    { value: 'hr_desc', label: 'ЧСС: по убыванию' },
    { value: 'hr_asc', label: 'ЧСС: по возрастанию' },
    { value: 'quality_best', label: 'Качество: лучшее' },
    { value: 'quality_worst', label: 'Качество: хуже' },
];

const QUALITY_RANK: Record<EcgQualityVerdict, number> = {
    good: 0,
    acceptable: 1,
    warning: 2,
    retake: 3,
    bad: 4,
    unknown: 5,
};

/** Ключ времени в едином формате конфигурации (лексикографическая сортировка). */
function timestampKey(item: EcgStudyListItem): string {
    return formatValue(item.createdAt, 'date:yyyy-MM-dd HH:mm:ss', 'timestamp') || item.createdAt;
}

/** Дата исследования YYYY-MM-DD — для сравнения с полями type=date. */
export function ecgStudyDate(item: EcgStudyListItem): string {
    return timestampKey(item).slice(0, 10);
}

/** patientLabel — имя пациента для поиска/подписи (UUID не подставляется). */
export function ecgStudyPatientLabel(item: EcgStudyListItem): string {
    return patientDisplayName(patientOfStudy(item));
}

/** matchEcgStudy — поиск по пациенту, названию исследования и заключению. */
export function matchEcgStudy(item: EcgStudyListItem, query: string): boolean {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const haystack = [
        ecgStudyPatientLabel(item),
        item.title,
        item.conclusion,
        item.statusLabel,
        item.sourceLabel,
    ]
        .join(' ')
        .toLowerCase();
    for (const term of needle.split(/\s+/)) {
        if (term && !haystack.includes(term)) return false;
    }
    return true;
}

export function filterEcgStudies(
    items: EcgStudyListItem[],
    filters: EcgStudyFilterState,
): EcgStudyListItem[] {
    return items.filter((item) => {
        if (!matchEcgStudy(item, filters.search)) return false;
        if (filters.conclusion === 'yes' && !item.conclusion.trim()) return false;
        if (filters.conclusion === 'no' && item.conclusion.trim()) return false;
        const date = ecgStudyDate(item);
        if (filters.dateFrom && date < filters.dateFrom) return false;
        if (filters.dateTo && date > filters.dateTo) return false;
        if (filters.status && item.statusCode !== filters.status) return false;
        if (filters.quality && item.qualityVerdict !== filters.quality) return false;
        return true;
    });
}

function compareByDate(a: EcgStudyListItem, b: EcgStudyListItem): number {
    return timestampKey(a).localeCompare(timestampKey(b));
}

function compareByHr(a: EcgStudyListItem, b: EcgStudyListItem, descending: boolean): number {
    const ah = a.measurements.hrBpm;
    const bh = b.measurements.hrBpm;
    if (ah === null && bh === null) return compareByDate(b, a);
    if (ah === null) return 1;
    if (bh === null) return -1;
    return descending ? bh - ah : ah - bh;
}

function compareByQuality(a: EcgStudyListItem, b: EcgStudyListItem, bestFirst: boolean): number {
    const aUnknown = a.qualityVerdict === 'unknown';
    const bUnknown = b.qualityVerdict === 'unknown';
    if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
    const diff = QUALITY_RANK[a.qualityVerdict] - QUALITY_RANK[b.qualityVerdict];
    return bestFirst ? diff : -diff;
}

export function sortEcgStudies(items: EcgStudyListItem[], sort: EcgStudySort): EcgStudyListItem[] {
    const sorted = [...items];
    sorted.sort((a, b) => {
        switch (sort) {
            case 'date_asc':
                return compareByDate(a, b);
            case 'hr_desc':
                return compareByHr(a, b, true);
            case 'hr_asc':
                return compareByHr(a, b, false);
            case 'quality_best':
                return compareByQuality(a, b, true) || compareByDate(b, a);
            case 'quality_worst':
                return compareByQuality(a, b, false) || compareByDate(b, a);
            case 'date_desc':
                return compareByDate(b, a);
        }
    });
    return sorted;
}
